import React, { useState } from "react";
import { fetchJson } from "../../lib/api";
import { isAmountSignUnexpectedForAccount } from "../../lib/account-flow";
import { ledgerTypes } from "../../lib/constants";
import type { Account, JournalEntry } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Input } from "../input";

export const JournalEntryEditorModal = ({
  houseId,
  accounts,
  journalEntry,
  onClose,
  onSaved,
}: {
  houseId: number;
  accounts: Account[];
  journalEntry: JournalEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const { t } = useI18n();
  const [accountId, setAccountId] = useState<string>(
    journalEntry?.accountId ? String(journalEntry.accountId) : "",
  );
  const [ledgerType, setLedgerType] = useState<JournalEntry["ledgerType"]>(
    journalEntry?.ledgerType ?? "actual",
  );
  const [amount, setAmount] = useState<string>(journalEntry ? String(journalEntry.amount) : "");
  const [currency, setCurrency] = useState<string>(journalEntry?.currency ?? "DKK");
  const [journalDate, setJournalDate] = useState<string>(journalEntry?.journalDate ?? "");
  const [channel, setChannel] = useState<string>(journalEntry?.channel ?? "");
  const [notes, setNotes] = useState<string>(journalEntry?.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteEntry = async () => {
    if (!journalEntry) {
      return;
    }
    const shouldDelete = window.confirm(
      t(
        "entries.deleteConfirm",
        "Delete this journal entry? This cannot be undone.",
      ),
    );
    if (!shouldDelete) {
      return;
    }
    setIsDeleting(true);
    setError(null);
    try {
      await fetchJson(`/api/houses/${houseId}/journal-entries/${journalEntry.id}`, {
        method: "DELETE",
      });
      onSaved();
      onClose();
    } catch (deleteError) {
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : t("entries.deleteFailed", "Failed to delete journal entry"),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const parsedAmount = Number.parseFloat(amount);
    if (!journalDate || Number.isNaN(parsedAmount)) {
      setError(t("entries.required", "Journal date and amount are required."));
      return;
    }
    const parsedAccountId = accountId ? Number.parseInt(accountId, 10) : Number.NaN;
    const selectedAccount =
      !Number.isNaN(parsedAccountId) ? (accounts.find((account) => account.id === parsedAccountId) ?? null) : null;
    if (isAmountSignUnexpectedForAccount(selectedAccount, parsedAmount)) {
      const displayName = selectedAccount
        ? (selectedAccount.labelKey ? t(selectedAccount.labelKey, selectedAccount.name) : selectedAccount.name) +
          " · " +
          selectedAccount.number
        : t("entries.account", "Account");
      const shouldContinue = window.confirm(
        t(
          "entries.amountSignWarning",
          "Warning: The amount sign looks unusual for {account}. Continue anyway?",
          { account: displayName },
        ),
      );
      if (!shouldContinue) {
        return;
      }
    }
    setIsSubmitting(true);
    try {
      const payload = {
        accountId: Number.isNaN(parsedAccountId) ? null : parsedAccountId,
        ledgerType,
        amount: parsedAmount,
        currency: currency.trim().toUpperCase(),
        journalDate,
        channel: channel.trim() || null,
        channelRef: journalEntry?.channelRef ?? null,
        notes: notes.trim() || null,
      };
      if (journalEntry) {
        await fetchJson(`/api/houses/${houseId}/journal-entries/${journalEntry.id}`, {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        await fetchJson(`/api/houses/${houseId}/journal-entries`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      onSaved();
      onClose();
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : t("entries.saveFailed", "Failed to save journal entry"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="relative">
          <CardTitle>
            {journalEntry
              ? t("entries.editTitle", "Edit journal entry")
              : t("entries.new", "New journal entry")}
          </CardTitle>
          <button
            type="button"
            className="absolute right-4 top-4 text-xl text-muted transition hover:text-ink"
            onClick={onClose}
            aria-label={t("common.close", "Close")}
          >
            ×
          </button>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <label className="text-sm font-medium text-ink" htmlFor="entry-account">
                  {t("entries.account", "Account")}
                </label>
                <select
                  id="entry-account"
                  className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus"
                  value={accountId}
                  onChange={(event) => setAccountId(event.target.value)}
                >
                  <option value="">{t("entries.noAccount", "No account")}</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {(account.labelKey ? t(account.labelKey, account.name) : account.name) +
                        " · " +
                        account.number}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-ink" htmlFor="entry-type">
                  {t("entries.ledgerType", "Ledger type")}
                </label>
                <select
                  id="entry-type"
                  className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus"
                  value={ledgerType}
                  onChange={(event) => setLedgerType(event.target.value as JournalEntry["ledgerType"])}
                >
                  {ledgerTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {t(type.labelKey, type.label)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-ink" htmlFor="entry-date">
                  {t("entries.journalDate", "Journal date")}
                </label>
                <Input
                  id="entry-date"
                  type="date"
                  value={journalDate}
                  onChange={(event) => setJournalDate(event.target.value)}
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-ink" htmlFor="entry-amount">
                  {t("bankTransactions.amount", "Amount")}
                </label>
                <Input
                  id="entry-amount"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  inputMode="decimal"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-ink" htmlFor="entry-currency">
                  {t("bankAccounts.currency", "Currency")}
                </label>
                <Input
                  id="entry-currency"
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-ink" htmlFor="entry-channel">
                {t("bankTransactions.channel", "Channel")}
              </label>
              <Input
                id="entry-channel"
                value={channel}
                onChange={(event) => setChannel(event.target.value)}
                placeholder={t("entries.channelPlaceholder", "Invoice, manual, etc.")}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-ink" htmlFor="entry-notes">
                {t("entries.notes", "Notes")}
              </label>
              <Input
                id="entry-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder={t("entries.notesPlaceholder", "Optional notes")}
              />
            </div>
            {error ? <p className="text-sm text-error">{error}</p> : null}
            <div className="flex justify-between gap-2">
              {journalEntry ? (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={deleteEntry}
                  disabled={isSubmitting || isDeleting}
                >
                  {isDeleting
                    ? t("common.working", "Working...")
                    : t("entries.delete", "Delete")}
                </Button>
              ) : (
                <span />
              )}
              <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t("common.cancel", "Cancel")}
              </Button>
                <Button type="submit" disabled={isSubmitting || isDeleting}>
                  {isSubmitting
                    ? t("common.saving", "Saving...")
                    : t("entries.save", "Save journal entry")}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
